import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TypeOf, z } from "zod";
import { Profile } from "../../../../../@types/profile";
import DelendCentralizedCircularProgress from "../../../../../components/CircularProgress/DelendCentralizedCircularProgress";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import ProfileService from "../../../../../services/ProfileService";
import ReturnButton from "../../ReturnButton";
import { add } from "../../../../../store/profileSlice";

const profileNameInput = z.object({
  name: z
    .string()
    .nonempty({ message: "Nome completo é obrigatório" })
    .refine(
      (name) => {
        const words = name.trim().split(/\s+/);
        return words.length >= 2;
      },
      {
        message: "Por favor inclua o nome e sobrenome completos",
      }
    ),
});

type ProfileNameInput = TypeOf<typeof profileNameInput>;

const ProfileHeader = () => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={1}>
      <Typography variant="h2">Nome</Typography>

      <Typography>
        Utilize seu nome completo. Se você for sócio ou representante legal,
        inclua seu nome assim como aparece no contrato social da empresa.
      </Typography>
    </Box>
  );
};

const ProfileNameEditor: React.FC = () => {
  const navigate = useNavigate();

  const profile = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  const dispatch = useAppDispatch();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const {
    register,
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<ProfileNameInput>({
    resolver: zodResolver(profileNameInput),
  });

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    clearErrors("name");
    setIsButtonDisabled(newValue === profile?.person.name);
  };

  if (!profile) return <DelendCentralizedCircularProgress />;

  const onSubmit: SubmitHandler<ProfileNameInput> = async (profileNameData) => {
    try {
      await ProfileService.updateProfile(profileNameData, profile.person.uuid);
      const profileUpdated = await ProfileService.getProfile();
      dispatch(add(profileUpdated));

      toast.success("Alterações salvas com sucesso!");
      navigate("/dashboard/profile");
    } catch (error) {
      toast.error("Erro ao salvar alterações");
    }
  };

  return (
    <Box display={"flex"} gap={2}>
      <ReturnButton to="/dashboard/profile" />
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        width={"100%"}
      >
        <ProfileHeader />
        <TextField
          {...register("name")}
          error={!!errors.name}
          onChange={handleTextChange}
          helperText={errors.name?.message}
          defaultValue={profile.person.name}
          variant="standard"
          fullWidth
          label="Nome completo"
          disabled={isSubmitting}
        />
        <Box display={"flex"} justifyContent={"end"}>
          <Button
            variant="outlined"
            size="large"
            disabled={isButtonDisabled || isSubmitting}
            type="submit"
            endIcon={isSubmitting && <CircularProgress size={20} />}
          >
            salvar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileNameEditor;
